import React, { useMemo, useState } from 'react';
import {
  useContracts,
  useClients,
  useTarifas,
  useComisionConfig,
  useConvenios,
  useAfianzadoras,
} from '../../contexts/DataContext';
import { useComisionLedger } from '../../hooks/useComisionLedger';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Badge } from '../../components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/table';
import TablePager from '../../components/TablePager';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  getFechaEmision,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
} from '../../utils/calculations';
import { indexMovementsByFianza, resolveComision } from '../../utils/comisionReconcile';
import { mapAfianzadoraName } from '../../utils/afianzadoras';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MONTHS_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const UNASSIGNED_KEY = '__unassigned__';
const PER_PAGE = 10;

const COLOR_ACTUAL = '#284865'; // azul-surelia
const COLOR_PREVIO = '#94a3b8';

// Same display mapping the afianzadora reportes use (LiquidacionComisiones has
// the complete list; the two producción ones carry a shorter copy).

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const fmtInt = (n) => new Intl.NumberFormat('es-MX').format(Number(n) || 0);

// Eje del chart: "0k" para todo lo que no llega a mil no dice nada.
const fmtCompact = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)}k`;
  return fmtInt(Math.round(v));
};
const fmtPct = (n, digits = 1) => `${(Number(n) || 0).toFixed(digits)}%`;

const titleCase = (str) =>
  String(str || '')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// Métricas del comparativo. La comisión va primero: el reporte se lee desde el
// dinero que gana el despacho, no desde el volumen.
const METRICS = [
  { key: 'comision', label: 'Comisión', money: true },
  { key: 'prima', label: 'Prima', money: true },
  { key: 'monto', label: 'Monto afianzado', money: true },
  { key: 'count', label: 'Fianzas', money: false },
];

const DIMENSIONS = [
  { key: 'afianzadora', label: 'Afianzadora' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'responsable', label: 'Responsable' },
  { key: 'tipo', label: 'Tipo' },
];

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'meses', label: 'Mes a mes' },
  { id: 'variacion', label: 'Variación' },
  { id: 'mix', label: 'Mix y concentración' },
];

const fmtMetric = (value, metric) => (metric.money ? fmtMXN(value) : fmtInt(value));

// Δ% contra una base que puede ser cero: sin base no hay porcentaje, y decir
// "+100%" donde antes no había nada es inventar una comparación.
const variacionPct = (actual, previo) => {
  if (!previo) return null;
  return ((actual - previo) / Math.abs(previo)) * 100;
};

function DeltaPct({ actual, previo, invertir = false, className = '' }) {
  const pct = variacionPct(actual, previo);
  if (pct === null) {
    return (
      <span className={`tabular-nums text-zinc-400 ${className}`}>
        {actual ? 'nuevo' : '—'}
      </span>
    );
  }
  const sube = pct > 0;
  const bueno = invertir ? !sube : sube;
  const color = Math.abs(pct) < 0.05 ? 'text-zinc-500' : bueno ? 'text-green-700' : 'text-red-600';
  return (
    <span className={`tabular-nums font-medium ${color} ${className}`}>
      {sube ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  );
}

// Tarjeta de KPI: el año seleccionado grande, el anterior y el Δ debajo. Sin el
// valor del año anterior a la vista el porcentaje no se puede auditar.
function YoYTile({ label, actual, previo, format, delta, sub }) {
  return (
    <div className="min-w-0 rounded-lg border border-azul-surelia/20 bg-white p-3">
      <div className="truncate text-[10px] uppercase tracking-wide text-gray-500" title={label}>
        {label}
      </div>
      <div
        className="truncate text-lg font-bold tabular-nums text-azul-surelia sm:text-xl"
        title={format(actual)}
      >
        {format(actual)}
      </div>
      <div className="mt-0.5 flex items-baseline gap-2 text-[11px]">
        <span className="truncate tabular-nums text-gray-500" title={format(previo)}>
          {format(previo)}
        </span>
        {delta || <DeltaPct actual={actual} previo={previo} />}
      </div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
}

function Pills({ options, value, onChange, ariaLabel }) {
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label={ariaLabel} data-pdf-hide>
      {options.map((opt) => (
        <button
          key={opt.key || opt.id}
          onClick={() => onChange(opt.key || opt.id)}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
            value === (opt.key || opt.id)
              ? 'bg-azul-surelia text-white'
              : 'bg-azul-surelia/5 text-azul-surelia/70 hover:bg-azul-surelia/10'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const blankBucket = () => ({ comision: 0, prima: 0, monto: 0, count: 0 });

const addTo = (bucket, f, peso = 1) => {
  bucket.comision += f.comision * peso;
  bucket.prima += f.prima * peso;
  bucket.monto += f.monto * peso;
  bucket.count += peso;
};

/**
 * ComparativoAnual — el ejercicio seleccionado contra el anterior.
 *
 * Producción y comisión salen de las fianzas (resolveComision: ledger cuando la
 * fianza tiene movimientos, al vuelo cuando no), fechadas por getFechaEmision,
 * que es la fecha de autorización. El bloque de cobro es lo único que se lee
 * directo del ledger, y va por fecha de movimiento, no de emisión.
 *
 * Anuladas y sustituidas quedan fuera; las canceladas se quedan, porque fueron
 * producción real. Es la misma regla del resto de los reportes.
 */
export function ComparativoAnual({ selectedYear }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsById, clientsLoading } = useClients();
  const { afianzadorasList } = useAfianzadoras();
  const { tarifas, tarifasLoading } = useTarifas();
  const { movements } = useComisionLedger();
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();
  const { getUserName } = useUser();

  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);
  const hoy = useMemo(() => new Date(), []);
  const anioPrevio = selectedYear - 1;
  const esAnioCorriente = selectedYear === hoy.getFullYear();

  const [activeTab, setActiveTab] = useState('resumen');
  const [metricKey, setMetricKey] = useState('comision');
  const [dimension, setDimension] = useState('afianzadora');
  const [page, setPage] = useState(1);
  // null = el default que corresponde al año; el usuario lo puede forzar.
  const [corteManual, setCorteManual] = useState(null);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  const aLaFecha = corteManual === null ? esAnioCorriente : corteManual === 'fecha';

  // Corte comparable: comparar un año corriente incompleto contra uno completo
  // da una caída que no existe. Con "a la fecha" los dos años se truncan al
  // mismo día.
  const dentroDelCorte = useMemo(() => {
    if (!aLaFecha) return () => true;
    const mes = hoy.getMonth();
    const dia = hoy.getDate();
    return (d) => d.getMonth() < mes || (d.getMonth() === mes && d.getDate() <= dia);
  }, [aLaFecha, hoy]);

  const mesTope = aLaFecha ? hoy.getMonth() : 11;

  // Una pasada sobre las fianzas de los dos años. Cada renglón trae ya las
  // claves de todas las dimensiones para no volver a recorrer los contratos.
  const fianzasYoY = useMemo(() => {
    const out = [];
    contractsList.forEach((contract) => {
      const splitOpts = {
        contractData: contract,
        orgConfig: comisionConfig,
        convenioData: contract.convenioId ? conveniosById?.[contract.convenioId] : null,
      };
      const client = clientsById[contract.clientId];
      // Los contratos de convenio no tienen cliente: el renglón es el convenio,
      // igual que en Ciclo de vida.
      const clienteLabel = contract.convenioId
        ? conveniosById?.[contract.convenioId]?.nombre || 'Convenio'
        : client?.nombreEmpresa || 'Cliente sin nombre';
      const clienteKey = contract.convenioId
        ? `convenio:${contract.convenioId}`
        : contract.clientId || 'sin-cliente';
      const responsables = [
        ...new Set([...(client?.assignedUsers || []), ...(contract.assignedUsers || [])]),
      ];

      (contract.fianzas || [])
        .filter((f) => f.estadoFianza !== 'anulada' && f.estadoFianza !== 'sustituida')
        .forEach((fianza) => {
          const emisionMs = getFechaEmision(fianza);
          if (!emisionMs) return;
          const d = new Date(emisionMs);
          const year = d.getFullYear();
          if (year !== selectedYear && year !== anioPrevio) return;

          const emisorRaw = getEmisorNombre(fianza);
          const { prima, comisionMonto } = resolveComision(
            fianza,
            byFianza.get(fianza.id),
            tarifas,
            emisorRaw,
            getSubRamo(fianza),
            getTipoFianza(fianza),
            splitOpts
          );

          out.push({
            year,
            month: d.getMonth(),
            fecha: d,
            monto: Number(fianza.montoAfianzado) || 0,
            prima: Number(prima) || 0,
            comision: Number(comisionMonto) || 0,
            // resolveComision devuelve null cuando no hay % que aplicar (sin
            // tabla de tarifas, o convenio sin pct confirmado). Suma como cero,
            // así que hay que contarlas aparte o la comisión del año se lee
            // baja sin que nada lo diga.
            sinComision: comisionMonto == null,
            afianzadora: mapAfianzadoraName(emisorRaw, afianzadorasList),
            clienteKey,
            clienteLabel,
            esConvenio: Boolean(contract.convenioId),
            // El tipo viene con el casing de cada afianzadora ("CUMPLIMIENTO DE
            // OBRA" y "Cumplimiento de Obra" son el mismo): la clave normaliza
            // y la etiqueta se pinta pareja.
            tipoKey: (getTipoFianza(fianza) || 'Sin tipo').trim().toUpperCase(),
            tipoLabel: titleCase(getTipoFianza(fianza) || 'Sin tipo'),
            responsables: responsables.length > 0 ? responsables : [UNASSIGNED_KEY],
          });
        });
    });
    return out;
  }, [
    contractsList,
    clientsById,
    afianzadorasList,
    conveniosById,
    comisionConfig,
    tarifas,
    byFianza,
    selectedYear,
    anioPrevio,
  ]);

  const enCorte = useMemo(
    () => fianzasYoY.filter((f) => dentroDelCorte(f.fecha)),
    [fianzasYoY, dentroDelCorte]
  );

  // Totales y serie mensual de cada año.
  const { actual, previo } = useMemo(() => {
    const build = () => ({
      ...blankBucket(),
      clientes: new Set(),
      convenios: new Set(),
      afianzadoras: new Set(),
      sinComision: 0,
      meses: Array.from({ length: 12 }, () => blankBucket()),
    });
    const acc = { actual: build(), previo: build() };
    enCorte.forEach((f) => {
      const bucket = f.year === selectedYear ? acc.actual : acc.previo;
      addTo(bucket, f);
      addTo(bucket.meses[f.month], f);
      (f.esConvenio ? bucket.convenios : bucket.clientes).add(f.clienteKey);
      bucket.afianzadoras.add(f.afianzadora);
      if (f.sinComision) bucket.sinComision += 1;
    });
    return acc;
  }, [enCorte, selectedYear]);

  const porFianza = (b, key) => (b.count > 0 ? b[key] / b.count : 0);

  // Curva acumulada: es la lectura de "cómo vamos" — el mes suelto sube y baja
  // por una sola fianza grande, el acumulado no.
  const chartData = useMemo(() => {
    let a = 0;
    let p = 0;
    const data = [];
    for (let m = 0; m <= mesTope; m += 1) {
      a += actual.meses[m][metric.key];
      p += previo.meses[m][metric.key];
      data.push({ mes: MONTHS[m], actual: a, previo: p });
    }
    return data;
  }, [actual, previo, metric.key, mesTope]);

  const mesesRows = useMemo(
    () =>
      Array.from({ length: mesTope + 1 }, (_, m) => ({
        mes: MONTHS_LARGOS[m],
        actual: actual.meses[m][metric.key],
        previo: previo.meses[m][metric.key],
      })),
    [actual, previo, metric.key, mesTope]
  );

  // Variación por dimensión. Responsable reparte la fianza entre sus asignados
  // (1/n) para que la suma de los renglones siga siendo el total del año — si
  // no, la columna de contribución al Δ mentiría.
  const variacionRows = useMemo(() => {
    const map = new Map();
    const ensure = (key, label) => {
      if (!map.has(key)) {
        map.set(key, { key, label, actual: blankBucket(), previo: blankBucket() });
      }
      return map.get(key);
    };

    enCorte.forEach((f) => {
      const targets =
        dimension === 'responsable'
          ? f.responsables.map((uid) => ({
              // El nombre se resuelve al pintar: usersCache se llena async y
              // getUserName cambia de identidad en cada render, así que
              // resolverlo aquí dejaría "Desconocido" congelado en el memo.
              key: uid,
              label: null,
              peso: 1 / f.responsables.length,
            }))
          : dimension === 'cliente'
          ? [{ key: f.clienteKey, label: f.clienteLabel, peso: 1 }]
          : dimension === 'tipo'
          ? [{ key: f.tipoKey, label: f.tipoLabel, peso: 1 }]
          : [{ key: f.afianzadora, label: f.afianzadora, peso: 1 }];

      targets.forEach(({ key, label, peso }) => {
        const entry = ensure(key, label);
        addTo(f.year === selectedYear ? entry.actual : entry.previo, f, peso);
      });
    });

    const deltaTotal = actual[metric.key] - previo[metric.key];
    return Array.from(map.values())
      .map((r) => {
        const a = r.actual[metric.key];
        const p = r.previo[metric.key];
        const delta = a - p;
        return {
          ...r,
          valorActual: a,
          valorPrevio: p,
          delta,
          // Qué parte del movimiento total explica este renglón. Con Δ total
          // cerca de cero el cociente se dispara y no dice nada, así que se
          // omite.
          contribucion: Math.abs(deltaTotal) > 0.005 ? (delta / deltaTotal) * 100 : null,
        };
      })
      .filter((r) => r.valorActual !== 0 || r.valorPrevio !== 0)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }, [enCorte, dimension, metric.key, selectedYear, actual, previo]);

  const pagedVariacion = variacionRows.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Los renglones de responsable guardan el uid; el nombre se resuelve aquí.
  const rowLabel = (r) => {
    if (r.label) return r.label;
    if (r.key === UNASSIGNED_KEY) return 'Sin asignar';
    return getUserName(r.key) || r.key;
  };

  // Mix por afianzadora: el peso de cada emisor en la métrica, los dos años.
  // El Δ va en puntos porcentuales — un mix es una repartición, no un importe.
  const mixRows = useMemo(() => {
    const map = new Map();
    enCorte.forEach((f) => {
      if (!map.has(f.afianzadora)) {
        map.set(f.afianzadora, { key: f.afianzadora, actual: blankBucket(), previo: blankBucket() });
      }
      addTo(map.get(f.afianzadora)[f.year === selectedYear ? 'actual' : 'previo'], f);
    });
    const totalA = actual[metric.key];
    const totalP = previo[metric.key];
    return Array.from(map.values())
      .map((r) => {
        const a = r.actual[metric.key];
        const p = r.previo[metric.key];
        const shareA = totalA > 0 ? (a / totalA) * 100 : 0;
        const shareP = totalP > 0 ? (p / totalP) * 100 : 0;
        return { ...r, valorActual: a, valorPrevio: p, shareA, shareP, deltaPp: shareA - shareP };
      })
      .filter((r) => r.valorActual !== 0 || r.valorPrevio !== 0)
      .sort((a, b) => b.valorActual - a.valorActual);
  }, [enCorte, metric.key, actual, previo, selectedYear]);

  // Concentración de clientes: cuánto del año depende de los de arriba. Subir
  // de año con más concentración es crecer y volverse más frágil a la vez.
  const concentracion = useMemo(() => {
    const porAnio = (year) => {
      const map = new Map();
      enCorte
        .filter((f) => f.year === year)
        .forEach((f) => {
          if (!map.has(f.clienteKey)) {
            map.set(f.clienteKey, { key: f.clienteKey, label: f.clienteLabel, ...blankBucket() });
          }
          addTo(map.get(f.clienteKey), f);
        });
      const rows = Array.from(map.values()).sort((a, b) => b[metric.key] - a[metric.key]);
      const total = rows.reduce((s, r) => s + r[metric.key], 0);
      const share = (n) =>
        total > 0 ? (rows.slice(0, n).reduce((s, r) => s + r[metric.key], 0) / total) * 100 : 0;
      // Cuántos clientes hacen el 80% del año.
      let acumulado = 0;
      let clientes80 = 0;
      for (const r of rows) {
        if (total <= 0 || acumulado / total >= 0.8) break;
        acumulado += r[metric.key];
        clientes80 += 1;
      }
      return { rows, total, top5: share(5), top10: share(10), clientes80 };
    };
    const a = porAnio(selectedYear);
    const p = porAnio(anioPrevio);
    const prevByKey = new Map(p.rows.map((r) => [r.key, r]));
    const top = a.rows.slice(0, 10).map((r) => ({
      key: r.key,
      label: r.label,
      valorActual: r[metric.key],
      valorPrevio: prevByKey.get(r.key)?.[metric.key] || 0,
      share: a.total > 0 ? (r[metric.key] / a.total) * 100 : 0,
    }));
    return { a, p, top };
  }, [enCorte, metric.key, selectedYear, anioPrevio]);

  // Cobro: lo único que se lee directo del ledger, por fecha de movimiento.
  // `adeudo` es dinero que el despacho debe devolver, no un cobro pendiente —
  // se mantiene aparte, como en Liquidación.
  //
  // Los importes NO son comparables año contra año: `estadoCobro` es el estado
  // de hoy, no una foto del cierre de cada ejercicio. Lo del año pasado está
  // casi todo cobrado porque pasó el tiempo, no porque se cobrara mejor, así
  // que un "% cobrado" de un año contra otro mide el calendario. Lo que sí
  // compara gestión son los días: cuánto tardó en cobrarse lo ya cobrado.
  const cobro = useMemo(() => {
    const build = () => ({ cobrada: 0, porCobrar: 0, adeudo: 0, diasSuma: 0, diasCount: 0 });
    const acc = { actual: build(), previo: build(), hayDatos: false };
    (movements || []).forEach((m) => {
      const ms = Number(m.fecha) || 0;
      if (!ms) return;
      const d = new Date(ms);
      const year = d.getFullYear();
      if (year !== selectedYear && year !== anioPrevio) return;
      if (!dentroDelCorte(d)) return;
      acc.hayDatos = true;
      const bucket = year === selectedYear ? acc.actual : acc.previo;
      const monto = Number(m.comisionMonto) || 0;
      if (m.estadoCobro === 'cobrada') {
        bucket.cobrada += monto;
        const cobradaMs = Number(m.cobradaFecha) || 0;
        // Un cobro fechado antes del movimiento es dato sucio, no un cobro
        // instantáneo: queda fuera del promedio.
        if (cobradaMs >= ms) {
          bucket.diasSuma += (cobradaMs - ms) / 86400000;
          bucket.diasCount += 1;
        }
      } else if (m.estadoCobro === 'pendiente') bucket.porCobrar += monto;
      else if (m.estadoCobro === 'adeudo') bucket.adeudo += Math.abs(monto);
    });
    return acc;
  }, [movements, selectedYear, anioPrevio, dentroDelCorte]);

  const diasCobro = (b) => (b.diasCount > 0 ? b.diasSuma / b.diasCount : null);
  const diasActual = diasCobro(cobro.actual);
  const diasPrevio = diasCobro(cobro.previo);
  const diasDelta = diasActual !== null && diasPrevio !== null ? diasActual - diasPrevio : null;

  const loading = contractsLoading || clientsLoading || tarifasLoading;

  const corteLabel = aLaFecha
    ? `al ${hoy.getDate()} de ${MONTHS_LARGOS[hoy.getMonth()]}`
    : 'año completo';

  const selectTab = (id) => {
    setActiveTab(id);
    setPage(1);
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-azul-surelia"></div>
      </div>
    );
  }

  const sinDatos = actual.count === 0 && previo.count === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">
            Comparativo anual
          </Heading>
          <Text className="text-sm text-azul-surelia/70">
            {selectedYear} contra {anioPrevio}, {corteLabel}.
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-pdf-hide>
          <Pills
            ariaLabel="Corte"
            options={[
              { id: 'fecha', label: 'A la fecha' },
              { id: 'anio', label: 'Año completo' },
            ]}
            value={aLaFecha ? 'fecha' : 'anio'}
            onChange={(id) => setCorteManual(id)}
          />
          <Badge color="blue">{selectedYear}</Badge>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-pdf-hide>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => selectTab(tab.id)}
              className={`rounded-lg border px-4 py-2 transition ${
                activeTab === tab.id
                  ? 'border-azul-surelia bg-azul-surelia text-white'
                  : 'border-azul-surelia/40 bg-white text-azul-surelia hover:border-azul-surelia/70'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab !== 'resumen' && (
          <Pills ariaLabel="Métrica" options={METRICS} value={metricKey} onChange={setMetricKey} />
        )}
      </div>

      {sinDatos ? (
        <div className="py-10 text-center text-sm text-zinc-500">
          No hay fianzas emitidas en {anioPrevio} ni en {selectedYear}.
        </div>
      ) : (
        <>
          {activeTab === 'resumen' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <YoYTile label="Comisión" actual={actual.comision} previo={previo.comision} format={fmtMXN} />
                <YoYTile label="Prima" actual={actual.prima} previo={previo.prima} format={fmtMXN} />
                <YoYTile
                  label="Monto afianzado"
                  actual={actual.monto}
                  previo={previo.monto}
                  format={fmtMXN}
                />
                <YoYTile label="Fianzas" actual={actual.count} previo={previo.count} format={fmtInt} />
                <YoYTile
                  label="Clientes con producción"
                  actual={actual.clientes.size}
                  previo={previo.clientes.size}
                  format={fmtInt}
                  sub={
                    actual.convenios.size + previo.convenios.size > 0
                      ? `+ ${fmtInt(actual.convenios.size)} convenios (${fmtInt(
                          previo.convenios.size
                        )} en ${anioPrevio})`
                      : null
                  }
                />
                <YoYTile
                  label="Comisión promedio por fianza"
                  actual={porFianza(actual, 'comision')}
                  previo={porFianza(previo, 'comision')}
                  format={fmtMXN}
                />
                <YoYTile
                  label="Prima promedio por fianza"
                  actual={porFianza(actual, 'prima')}
                  previo={porFianza(previo, 'prima')}
                  format={fmtMXN}
                />
              </div>

              {actual.sinComision + previo.sinComision > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {fmtInt(actual.sinComision)} fianza{actual.sinComision === 1 ? '' : 's'} de{' '}
                  {selectedYear} y {fmtInt(previo.sinComision)} de {anioPrevio} no tienen un
                  porcentaje de comisión que aplicar, así que suman cero: la comisión de esos años
                  está subestimada y esas fianzas no aparecen en Variación ni en Mix cuando la
                  métrica es comisión.
                </div>
              )}

              <div className="rounded-xl border border-azul-surelia/15 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Heading level={3} className="text-base text-azul-surelia">
                      Acumulado del año
                    </Heading>
                    <Text className="text-xs text-azul-surelia/60">
                      {metric.label} acumulada mes a mes, {corteLabel}.
                    </Text>
                  </div>
                  <Pills
                    ariaLabel="Métrica"
                    options={METRICS}
                    value={metricKey}
                    onChange={setMetricKey}
                  />
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => (metric.money ? fmtCompact(v) : fmtInt(v))}
                      />
                      <Tooltip formatter={(v) => fmtMetric(v, metric)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line
                        type="monotone"
                        dataKey="previo"
                        name={String(anioPrevio)}
                        stroke={COLOR_PREVIO}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        name={String(selectedYear)}
                        stroke={COLOR_ACTUAL}
                        strokeWidth={2.5}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {cobro.hayDatos && (
                <div className="rounded-xl border border-azul-surelia/15 bg-white p-4 shadow-sm">
                  <div className="mb-3">
                    <Heading level={3} className="text-base text-azul-surelia">
                      Cobro de la comisión
                    </Heading>
                    <Text className="text-xs text-azul-surelia/60">
                      Del ledger, por fecha de movimiento. Los importes son el estado de hoy, no el
                      cierre de cada año: de {anioPrevio} ya está casi todo cobrado porque pasó el
                      tiempo, no porque se cobrara mejor. El renglón que sí compara los dos años es
                      el de días.
                    </Text>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-azul-surelia text-xs uppercase tracking-wide text-white">
                          <th className="py-2 pr-4 text-left font-medium"></th>
                          <th className="py-2 pr-4 text-right font-medium">{anioPrevio}</th>
                          <th className="py-2 text-right font-medium">{selectedYear}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-azul-surelia/10">
                        {[
                          { label: 'Cobrada', prev: cobro.previo.cobrada, act: cobro.actual.cobrada },
                          { label: 'Sin cobrar', prev: cobro.previo.porCobrar, act: cobro.actual.porCobrar },
                          { label: 'Adeudo', prev: cobro.previo.adeudo, act: cobro.actual.adeudo },
                        ].map((r) => (
                          <tr key={r.label}>
                            <td className="py-2 pr-4">{r.label}</td>
                            <td className="py-2 pr-4 text-right tabular-nums text-zinc-500">
                              {fmtMXN(r.prev)}
                            </td>
                            <td className="py-2 text-right font-medium tabular-nums">
                              {fmtMXN(r.act)}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-azul-surelia/5">
                          <td className="py-2 pr-4">
                            Días promedio de cobro
                            <span className="ml-2 text-[11px] text-gray-500">
                              sobre lo que ya se cobró
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-right tabular-nums text-zinc-500">
                            {diasPrevio === null
                              ? '—'
                              : `${Math.round(diasPrevio)} d (${cobro.previo.diasCount})`}
                          </td>
                          <td className="py-2 text-right font-medium tabular-nums">
                            {diasActual === null
                              ? '—'
                              : `${Math.round(diasActual)} d (${cobro.actual.diasCount})`}
                            {diasDelta !== null && (
                              <span
                                className={`ml-2 text-xs font-medium ${
                                  Math.abs(diasDelta) < 1
                                    ? 'text-zinc-500'
                                    : diasDelta > 0
                                    ? 'text-red-600'
                                    : 'text-green-700'
                                }`}
                              >
                                {diasDelta > 0 ? '+' : '−'}
                                {Math.abs(Math.round(diasDelta))} d
                              </span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'meses' && (
            <div className="overflow-x-auto rounded-xl border border-azul-surelia/15 bg-white">
              <Table dense>
                <TableHead className="bg-azul-surelia text-white">
                  <TableRow>
                    <TableHeader className="text-white">Mes</TableHeader>
                    <TableHeader className="text-white text-right">{anioPrevio}</TableHeader>
                    <TableHeader className="text-white text-right">{selectedYear}</TableHeader>
                    <TableHeader className="text-white text-right">Δ</TableHeader>
                    <TableHeader className="text-white text-right">Δ%</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mesesRows.map((r) => (
                    <TableRow key={r.mes}>
                      <TableCell className="capitalize">{r.mes}</TableCell>
                      <TableCell className="text-right tabular-nums text-zinc-500">
                        {fmtMetric(r.previo, metric)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {fmtMetric(r.actual, metric)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.actual - r.previo >= 0 ? '+' : '−'}
                        {fmtMetric(Math.abs(r.actual - r.previo), metric)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DeltaPct actual={r.actual} previo={r.previo} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-azul-surelia/5 font-semibold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMetric(previo[metric.key], metric)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMetric(actual[metric.key], metric)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {actual[metric.key] - previo[metric.key] >= 0 ? '+' : '−'}
                      {fmtMetric(Math.abs(actual[metric.key] - previo[metric.key]), metric)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaPct actual={actual[metric.key]} previo={previo[metric.key]} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {activeTab === 'variacion' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text className="text-xs text-azul-surelia/60">
                  Ordenado por tamaño del movimiento. La contribución es qué parte del Δ total
                  explica cada renglón
                  {dimension === 'responsable'
                    ? '; una fianza con varios responsables se reparte entre ellos.'
                    : '.'}
                </Text>
                <Pills
                  ariaLabel="Dimensión"
                  options={DIMENSIONS}
                  value={dimension}
                  onChange={(d) => {
                    setDimension(d);
                    setPage(1);
                  }}
                />
              </div>
              <div className="overflow-x-auto rounded-xl border border-azul-surelia/15 bg-white">
                <Table dense>
                  <TableHead className="bg-azul-surelia text-white">
                    <TableRow>
                      <TableHeader className="text-white">
                        {DIMENSIONS.find((d) => d.key === dimension)?.label}
                      </TableHeader>
                      <TableHeader className="text-white text-right">{anioPrevio}</TableHeader>
                      <TableHeader className="text-white text-right">{selectedYear}</TableHeader>
                      <TableHeader className="text-white text-right">Δ</TableHeader>
                      <TableHeader className="text-white text-right">Δ%</TableHeader>
                      <TableHeader className="text-white text-right">Contribución</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedVariacion.map((r) => (
                      <TableRow key={r.key}>
                        <TableCell className="max-w-[240px] truncate" title={rowLabel(r)}>
                          {rowLabel(r)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-zinc-500">
                          {fmtMetric(r.valorPrevio, metric)}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {fmtMetric(r.valorActual, metric)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.delta >= 0 ? '+' : '−'}
                          {fmtMetric(Math.abs(r.delta), metric)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DeltaPct actual={r.valorActual} previo={r.valorPrevio} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-zinc-500">
                          {r.contribucion === null ? '—' : fmtPct(r.contribucion, 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="px-3">
                  <TablePager
                    total={variacionRows.length}
                    page={page}
                    onPage={setPage}
                    perPage={PER_PAGE}
                    itemLabel="renglones"
                    compact
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mix' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <YoYTile
                  label="Top 5 clientes"
                  actual={concentracion.a.top5}
                  previo={concentracion.p.top5}
                  format={(v) => fmtPct(v)}
                  delta={
                    <span className="tabular-nums text-gray-500">
                      {concentracion.a.top5 - concentracion.p.top5 >= 0 ? '+' : ''}
                      {(concentracion.a.top5 - concentracion.p.top5).toFixed(1)} pp
                    </span>
                  }
                  sub={`de la ${metric.label.toLowerCase()}`}
                />
                <YoYTile
                  label="Top 10 clientes"
                  actual={concentracion.a.top10}
                  previo={concentracion.p.top10}
                  format={(v) => fmtPct(v)}
                  delta={
                    <span className="tabular-nums text-gray-500">
                      {concentracion.a.top10 - concentracion.p.top10 >= 0 ? '+' : ''}
                      {(concentracion.a.top10 - concentracion.p.top10).toFixed(1)} pp
                    </span>
                  }
                  sub={`de la ${metric.label.toLowerCase()}`}
                />
                <YoYTile
                  label="Clientes para el 80%"
                  actual={concentracion.a.clientes80}
                  previo={concentracion.p.clientes80}
                  format={fmtInt}
                  delta={
                    <span className="tabular-nums text-gray-500">
                      {concentracion.a.clientes80 - concentracion.p.clientes80 >= 0 ? '+' : ''}
                      {concentracion.a.clientes80 - concentracion.p.clientes80}
                    </span>
                  }
                />
                <YoYTile
                  label="Afianzadoras activas"
                  actual={actual.afianzadoras.size}
                  previo={previo.afianzadoras.size}
                  format={fmtInt}
                />
              </div>

              <div className="overflow-x-auto rounded-xl border border-azul-surelia/15 bg-white">
                <div className="border-b border-azul-surelia/10 px-4 py-3">
                  <Heading level={3} className="text-base text-azul-surelia">
                    Mix por afianzadora
                  </Heading>
                  <Text className="text-xs text-azul-surelia/60">
                    Peso de cada emisor en la {metric.label.toLowerCase()} del año. El Δ va en
                    puntos porcentuales.
                  </Text>
                </div>
                <Table dense>
                  <TableHead className="bg-azul-surelia text-white">
                    <TableRow>
                      <TableHeader className="text-white">Afianzadora</TableHeader>
                      <TableHeader className="text-white text-right">{anioPrevio}</TableHeader>
                      <TableHeader className="text-white text-right">{selectedYear}</TableHeader>
                      <TableHeader className="text-white text-right">% {anioPrevio}</TableHeader>
                      <TableHeader className="text-white text-right">% {selectedYear}</TableHeader>
                      <TableHeader className="text-white text-right">Δ pp</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mixRows.map((r) => (
                      <TableRow key={r.key}>
                        <TableCell>{r.key}</TableCell>
                        <TableCell className="text-right tabular-nums text-zinc-500">
                          {fmtMetric(r.valorPrevio, metric)}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {fmtMetric(r.valorActual, metric)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-zinc-500">
                          {fmtPct(r.shareP)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtPct(r.shareA)}
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums font-medium ${
                            Math.abs(r.deltaPp) < 0.05
                              ? 'text-zinc-500'
                              : r.deltaPp > 0
                              ? 'text-green-700'
                              : 'text-red-600'
                          }`}
                        >
                          {r.deltaPp >= 0 ? '+' : ''}
                          {r.deltaPp.toFixed(1)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="overflow-x-auto rounded-xl border border-azul-surelia/15 bg-white">
                <div className="border-b border-azul-surelia/10 px-4 py-3">
                  <Heading level={3} className="text-base text-azul-surelia">
                    Clientes que más pesan en {selectedYear}
                  </Heading>
                  <Text className="text-xs text-azul-surelia/60">
                    Los diez primeros por {metric.label.toLowerCase()}, con lo que hicieron en{' '}
                    {anioPrevio}.
                  </Text>
                </div>
                <Table dense>
                  <TableHead className="bg-azul-surelia text-white">
                    <TableRow>
                      <TableHeader className="text-white">Cliente</TableHeader>
                      <TableHeader className="text-white text-right">{anioPrevio}</TableHeader>
                      <TableHeader className="text-white text-right">{selectedYear}</TableHeader>
                      <TableHeader className="text-white text-right">% del año</TableHeader>
                      <TableHeader className="text-white text-right">Δ%</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {concentracion.top.map((r) => (
                      <TableRow key={r.key}>
                        <TableCell className="max-w-[260px] truncate" title={r.label}>
                          {r.label}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-zinc-500">
                          {fmtMetric(r.valorPrevio, metric)}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {fmtMetric(r.valorActual, metric)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtPct(r.share)}</TableCell>
                        <TableCell className="text-right">
                          <DeltaPct actual={r.valorActual} previo={r.valorPrevio} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ComparativoAnual;
